import type { FormValidateOrFn } from '@tanstack/form-core'
import { useForm } from '@tanstack/react-form'
import { useNavigate } from '@tanstack/react-router'
import type { LinkOptions } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { twMerge } from 'tailwind-merge'
import type { z } from 'zod'
import { FormActionBar } from '@/components/shared/form/FormActionBar'
import { uniqueFormattedFormErrors } from '@/components/shared/form/formatError'
import type { FormApi } from '@/components/shared/form/types'
import { buttonStyles } from '@/components/shared/links/styles'
import { SmallSpinner } from '@/components/shared/Spinner/SmallSpinner'
import { getErrorMessage } from '@/components/shared/toast/toastError'
import { toastSuccess } from '@/components/shared/toast/toastSuccess'
import type { Router } from '@/router'

type AppLinkTo = LinkOptions<Router>['to']

export type SubmitResult<T = Record<string, unknown>> =
  | {
      success: true
      message?: string
      redirect?: AppLinkTo
      search?: Record<string, unknown>
      resetValues?: T
    }
  | {
      success: false
      message: string
      errors?: Partial<Record<Extract<keyof T, string>, string[]>>
    }

/**
 * Shows server errors on their rendered fields; returns the messages that have no such field.
 * Field `errors` are derived from `errorMap`, and `onSubmit` entries clear on the next valid change.
 */
function applyFieldErrors(
  form: { setFieldMeta: unknown; getFieldInfo: unknown },
  errors: Partial<Record<string, string[]>> | undefined,
) {
  if (!errors) return []
  const getFieldInfo = form.getFieldInfo as (field: string) => { instance: unknown }
  const setFieldMeta = form.setFieldMeta as (
    field: string,
    updater: (prev: { errorMap?: Record<string, unknown> }) => {
      errorMap: Record<string, unknown>
    },
  ) => void
  const unassignedMessages: string[] = []
  for (const key of Object.keys(errors)) {
    const messages = errors[key]
    if (!messages?.length) continue
    if (!getFieldInfo(key).instance) {
      unassignedMessages.push(...messages)
      continue
    }
    setFieldMeta(key, (prev) => ({
      ...prev,
      errorMap: { ...prev.errorMap, onSubmit: messages.join(', ') },
    }))
  }
  return unassignedMessages
}

/** Use schema input shape for field values (differs from `z.infer` when the schema uses `.transform()`). */
type ActionBarPlacement = 'bottom' | 'both' | 'none'

type FormRenderState = {
  /** Message of the last rejected, invalid or failed submit, `null` otherwise. */
  submitError: string | null
}

type FormProps<TValues extends Record<string, unknown>> = {
  /**
   * `'none'` renders no action bar and no inline submit error; put your own submit button in
   * `children` and show `submitError` there (e.g. `AdminFormAsideActions`).
   */
  actionBarPlacement?: ActionBarPlacement
  actionBarRight?: ReactNode
  defaultValues: TValues
  schema: z.ZodTypeAny
  onSubmit: (values: TValues) => undefined | Promise<SubmitResult<TValues> | undefined>
  children: (form: FormApi<TValues>, state: FormRenderState) => ReactNode
  /** Associates an external submit button via the HTML `form` attribute. */
  id?: string
  className?: string
  /** Renders the standard `FormActionBar` submit button. Omit for a custom submit button in `children`. */
  submitLabel?: string
  submitClassName?: string
  showFormErrors?: boolean
}

export function Form<TValues extends Record<string, unknown>>({
  actionBarPlacement = 'bottom',
  actionBarRight,
  defaultValues,
  schema,
  onSubmit,
  submitLabel,
  submitClassName,
  showFormErrors = true,
  children,
  id,
  className,
}: FormProps<TValues>) {
  const navigate = useNavigate()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm<
    TValues,
    undefined,
    FormValidateOrFn<TValues>,
    undefined,
    undefined,
    undefined,
    FormValidateOrFn<TValues>,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined
  >({
    defaultValues,
    validators: {
      onChange: schema as FormValidateOrFn<TValues>,
      onSubmit: schema as FormValidateOrFn<TValues>,
    },
    onSubmitInvalid: ({ formApi }) => {
      const message =
        uniqueFormattedFormErrors(formApi.state.errors).join(' ') ||
        'Bitte korrigieren Sie die Fehler im Formular'
      console.error('[Form] submit invalid', formApi.getAllErrors())
      setSubmitError(message)
    },
    onSubmit: async ({ value }) => {
      setSubmitError(null)
      let result: SubmitResult<TValues> | undefined
      try {
        result = await onSubmit(value)
      } catch (error) {
        console.error('[Form] submit failed', error)
        setSubmitError(getErrorMessage(error, 'Ein Fehler ist aufgetreten'))
        return
      }
      if (!result) return
      if (result.success) {
        form.reset(result.resetValues ?? value)
        toastSuccess(result.message ?? 'Gespeichert.')
        if (result.redirect) {
          navigate({
            to: result.redirect,
            search: result.search,
          })
        }
        return
      }
      console.error('[Form] submit rejected:', result.message, result.errors)
      const unassignedMessages = applyFieldErrors(form, result.errors)
      setSubmitError([result.message, ...unassignedMessages].join(' '))
    },
  })

  const actionBar =
    actionBarPlacement !== 'none' && submitLabel ? (
      <FormActionBar
        left={
          <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting, s.errors] as const}>
            {([canSubmit, isSubmitting, errors]) => {
              const lines = showFormErrors ? uniqueFormattedFormErrors(errors) : []
              return (
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    disabled={!canSubmit || isSubmitting}
                    className={submitClassName ?? buttonStyles}
                    title={
                      !canSubmit && lines.length > 0
                        ? `Formular unvollständig: ${lines.join(' · ')}`
                        : undefined
                    }
                  >
                    {isSubmitting ? <SmallSpinner /> : null}
                    {submitLabel}
                  </button>
                  {lines.length > 0 ? (
                    <div className="min-w-0 text-sm text-red-800" role="alert">
                      {lines.map((msg) => (
                        <p key={msg}>{msg}</p>
                      ))}
                    </div>
                  ) : null}
                </div>
              )
            }}
          </form.Subscribe>
        }
        right={actionBarRight}
      />
    ) : null

  return (
    <form
      id={id}
      method="post"
      className={twMerge('space-y-6', className)}
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
    >
      {actionBarPlacement === 'both' ? actionBar : null}

      {children(form as FormApi<TValues>, { submitError })}

      {submitError && actionBarPlacement !== 'none' ? (
        <div className="text-sm text-red-600" role="alert">
          {submitError}
        </div>
      ) : null}

      {actionBar}
    </form>
  )
}
